import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TextCapitalized,
  Option,
  SelectDS,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  activateLimitBalanceDirectDebitAsync,
  activateStatementDirectDebitAsync,
  deactivateDirectDebitAsync,
  selectCompaniesSepaDirectDebitData,
} from "../sepaDirectDebitSlice";
import { SepaDirectDebitType, StatementFrequency } from "../sepaDirectDebitApi";
import { StatementOptions } from "./statementOptions";
import { LimitBalanceOptions } from "./limitBalanceOptions";
import { selectAdminUser } from "../../homeSlice";

export const ChangeSepaDirectDebitTypeModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  currentSepaDirectDebitCompanyUuid: string;
}) => {
  const { t } = useTranslation();
  const { visible, onCloseModal, currentSepaDirectDebitCompanyUuid } = props;
  const adminUser = useAppSelector(selectAdminUser);
  const dispatch = useAppDispatch();
  const [selectedType, setSelectedType] =
    useState<Option<SepaDirectDebitType> | null>(null);
  const [lowerLimit, setLowerLimit] = useState<number | "">("");
  const [upperLimit, setUpperLimit] = useState<number | "">("");
  const [statementFrequency, setStatementFrequency] =
    useState<Option<StatementFrequency> | null>(null);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const sepaDirectDebitList = useAppSelector(
    selectCompaniesSepaDirectDebitData,
  );

  const isLimitBalanceType = useMemo(() => {
    return selectedType && selectedType.value === "LIMIT_BALANCE";
  }, [selectedType]);

  const isStatementType = useMemo(() => {
    return selectedType && selectedType.value === "STATEMENT";
  }, [selectedType]);

  const isDeactivatedType = useMemo(() => {
    return selectedType && selectedType.value === "DEACTIVATED";
  }, [selectedType]);

  const resetModal = useCallback(() => {
    setDisplayError(false);
    setSelectedType(null);
    setLowerLimit("");
    setUpperLimit("");
    setStatementFrequency(null);
    onCloseModal();
  }, [onCloseModal]);

  const isFormValidForStatement = useMemo(() => {
    return isStatementType && statementFrequency !== null;
  }, [statementFrequency, isStatementType]);

  const isFormValidForLimitBalance = useMemo(() => {
    return (
      isLimitBalanceType &&
      lowerLimit !== "" &&
      upperLimit !== "" &&
      lowerLimit < upperLimit
    );
  }, [lowerLimit, upperLimit, isLimitBalanceType]);

  const isFormValid = useMemo(() => {
    if (!selectedType) return false;
    return (
      isFormValidForStatement || isFormValidForLimitBalance || isDeactivatedType
    );
  }, [
    selectedType,
    isFormValidForStatement,
    isFormValidForLimitBalance,
    isDeactivatedType,
  ]);

  const clickToSubmit = useCallback(() => {
    setDisplayError(true);

    if (!adminUser) {
      return;
    }

    if (isLimitBalanceType) {
      if (
        !isFormValidForLimitBalance ||
        lowerLimit === "" ||
        upperLimit === ""
      ) {
        return;
      }
      void dispatch(
        activateLimitBalanceDirectDebitAsync({
          companyUuid: currentSepaDirectDebitCompanyUuid,
          lowerLimit: lowerLimit,
          upperLimit: upperLimit,
          updatedBy: adminUser.email,
        }),
      );
    } else if (isStatementType && statementFrequency) {
      void dispatch(
        activateStatementDirectDebitAsync({
          companyUuid: currentSepaDirectDebitCompanyUuid,
          frequency: statementFrequency.value,
          updatedBy: adminUser.email,
        }),
      );
    } else if (isDeactivatedType) {
      void dispatch(
        deactivateDirectDebitAsync({
          companyUuid: currentSepaDirectDebitCompanyUuid,
          updatedBy: adminUser.email,
        }),
      );
    }
    resetModal();
  }, [
    adminUser,
    currentSepaDirectDebitCompanyUuid,
    dispatch,
    isDeactivatedType,
    isFormValidForLimitBalance,
    isLimitBalanceType,
    isStatementType,
    lowerLimit,
    resetModal,
    statementFrequency,
    upperLimit,
  ]);

  const currentSepaDirectDebit = useMemo(() => {
    return sepaDirectDebitList.find(
      (t) => t.companyUuid === currentSepaDirectDebitCompanyUuid,
    );
  }, [sepaDirectDebitList, currentSepaDirectDebitCompanyUuid]);

  const optionsSepaDirectDebitType = useMemo(() => {
    if (!currentSepaDirectDebit) return [];

    const types: SepaDirectDebitType[] = [
      "STATEMENT",
      "LIMIT_BALANCE",
      "DEACTIVATED",
    ];

    return types.map((type) => ({
      value: type,
      label: t(`sepaDirectDebit.sepaDirectDebitType.${type}`),
      disabled: currentSepaDirectDebit.sepaDirectDebitType === type,
    })) as Option<SepaDirectDebitType>[];
  }, [t, currentSepaDirectDebit]);

  if (!currentSepaDirectDebit) {
    return;
  }

  return (
    <DialogModalDS
      visible={visible}
      onClose={resetModal}
      title={t("changeSepaDirectDebitType.modal.title")}
      width={40}
      bottomButton={
        <ButtonDS
          text={t(`changeSepaDirectDebitType.modal.confirm`)}
          format={"fill"}
          buttonType={"primary"}
          disabled={!isFormValid}
          onClick={clickToSubmit}
        />
      }
    >
      <StyledColumn>
        <StyledRow>
          <TextCapitalized>
            {t(`changeSepaDirectDebitType.modal.type`)}
          </TextCapitalized>
          <Spacer x={0.5} />
          <TextCapitalized>
            {t(
              `sepaDirectDebit.sepaDirectDebitType.${currentSepaDirectDebit.sepaDirectDebitType}`,
            )}
          </TextCapitalized>
        </StyledRow>
        <Spacer y={1} />
        <SelectDS
          label={""}
          options={optionsSepaDirectDebitType}
          onChange={(selectedOption) => {
            setSelectedType(selectedOption ?? null);
          }}
          value={selectedType}
          allWidth
          withoutSpacerBottom
          error={
            displayError && selectedType === null
              ? t("changeSepaDirectDebitType.modal.emptySelect.error")
              : undefined
          }
        />
        {isLimitBalanceType && (
          <LimitBalanceOptions
            lowerLimit={lowerLimit}
            setLowerLimit={setLowerLimit}
            upperLimit={upperLimit}
            setUpperLimit={setUpperLimit}
            displayError={displayError}
          />
        )}
        {isStatementType && (
          <StatementOptions
            statementFrequency={statementFrequency}
            setStatementFrequency={setStatementFrequency}
            displayError={displayError}
          />
        )}
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
`;
